import {
  imgElementSvg,
  mapMarkerPinWrapper,
  mapEventMarkerWrapper,
} from '../../components/shared/vector-images';
import { telemetry } from '../backend';
import { captureEvent } from '../event-capture';
import { ROUTE_MARKER_SUMMARY } from '../route/route-names';
import { transitionToNextRoute } from '../route/route-navigation';
import { getStore } from '../store';

// We need to preload the disc colours, because the images are created and stored in Mapbox BEFORE the locations of markers are loaded
const waMooringMarkerDiscColours = window?.DECKEE_CONFIG?.markers
  ?.indicatorDotColours ?? [
  '#e34c53',
  '#a43f9c',
  '#6c787e',
  '#299959',
  '#319ec6',
  '#FFFFFF',
  '#000000',
  '#FFC0CB',
];

const waMooringMarkerCases = ['case'];
waMooringMarkerDiscColours.forEach((colour) => {
  waMooringMarkerCases.push(['==', ['get', 'discColour'], colour]);
  waMooringMarkerCases.push(`mooringMarker-${colour}`);
});

export const sourceExists = (mapReference, sourceName) =>
  !!mapReference?.getSource(sourceName);

export const setDataSource = (mapReference, sourceName, geojson) => {
  if (sourceExists(mapReference, sourceName)) {
    mapReference.getSource(sourceName).setData(geojson);
  }
};

export const layerExists = (map, layerName) => {
  try {
    const { layers } = map.getStyle();
    const requiredLayer = layers.filter((layer) => layer.id === layerName);
    return !!requiredLayer.length;
  } catch (error) {
    // Sometimes mapbox breaks and throws errors during getStyle(). Only way around this is to
    // catch and ignore the errors and hopefully the calling function will update on the next go.
    // This happens especially when the distanceTool is active and the user switches between
    // base map layers.
    return false;
  }
};

export const showPopOverFromEvent =
  (layer) => (/* map, controls */) => (event) => {
    const { markerType, id } = event.features[0].properties;
    telemetry(
      telemetry.DEBUG,
      `[showPopOverFromEvent] event on layer '${layer}': ${Object.keys(
        event,
      )} features ${Object.keys(event.features)}`,
    );
    transitionToNextRoute(ROUTE_MARKER_SUMMARY, { markerType, id });
  };

export const onInitializeStandardMarker = (layer) => async (map) => {
  const {
    standardMarkerOptions: { markerType },
    icon: { baseShape, iconColor, markerColor },
  } = layer;

  if (!sourceExists(map, 'standardMarkerLayer')) {
    map.addSource('standardMarkerLayer', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }

  if (!layerExists(map, 'standardMarkerLayer')) {
    map.addLayer(
      {
        id: 'standardMarkerLayer',
        type: 'symbol',
        source: 'standardMarkerLayer',
        layout: {
          'icon-image': [
            ...waMooringMarkerCases,
            ['get', 'markerImage'], // default marker types
          ],
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10.9999,
            [
              'case',
              ['==', ['get', 'markerSelected'], true],
              0.6,
              ['get', 'markerSize1'],
            ],
            11,
            [
              'case',
              ['==', ['get', 'markerSelected'], true],
              0.6,
              ['get', 'markerSize2'],
            ],
            14,
            [
              'case',
              ['==', ['get', 'markerSelected'], true],
              0.6,
              ['get', 'markerSize3'],
            ],
          ],
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'text-allow-overlap': true,
        },
        paint: {},
      },
      'storyMarker',
    );

    map.on('deckee-press', 'standardMarkerLayer', async (event) => {
      const { markerType: eventEntityType, id } = event.features[0].properties;
      telemetry(
        telemetry.DEBUG,
        `[standardMarkerEvent] event on standardMarkerLayer for markerType '${eventEntityType}': ${Object.keys(
          event,
        )}`,
      );

      await captureEvent({
        type: 'marker: click',
        payload: {
          markerType: eventEntityType,
          id,
          clickedLatitude: event.lngLat.lat,
          clickedLongitude: event.lngLat.lng,
        },
      });

      transitionToNextRoute(ROUTE_MARKER_SUMMARY, {
        markerType: eventEntityType,
        id,
      });

      // Prevent the map click event from propagating to other layers
      event.originalEvent.preventDefault();
    });
  }

  // Create the markers for each of the WA mooring disc colours
  let addMapMarkerImagesPromises = [];
  if (markerType === 'mooringMarker') {
    addMapMarkerImagesPromises = waMooringMarkerDiscColours.map(
      async (colour) => {
        const marker = await imgElementSvg(mapMarkerPinWrapper(baseShape()), {
          fillColor: iconColor,
          markerColor,
          discColour: colour,
          width: 90,
          height: 90,
        });
        marker.width = '90';
        marker.height = '90';

        if (!map.hasImage(`${markerType}-${colour}`))
          map.addImage(`${markerType}-${colour}`, marker);
      },
    );
  }

  addMapMarkerImagesPromises.push(
    (async () => {
      let marker;
      if (markerType === 'eventMarker') {
        marker = await imgElementSvg(mapEventMarkerWrapper(baseShape()), {
          fillColor: iconColor,
          markerColor,
          width: 140,
          height: 140,
        });
        marker.width = '140';
        marker.height = '140';
      } else {
        marker = await imgElementSvg(mapMarkerPinWrapper(baseShape()), {
          fillColor: iconColor,
          markerColor,
          width: 90,
          height: 90,
        });
        marker.width = '90';
        marker.height = '90';
      }

      if (!map.hasImage(markerType)) map.addImage(markerType, marker);
    })(),
  );

  await Promise.allSettled(addMapMarkerImagesPromises);
};

export const layerVisible = (map, layerName) => {
  if (!layerExists(map, layerName)) return false;
  const currentState = map.getLayoutProperty(layerName, 'visibility');
  return currentState === 'visible';
};

export const toggleLayerIfRequired = (map, layerName, show) => {
  if (!layerExists(map, layerName)) return;
  const layerIsVisible = layerVisible(map, layerName);
  if ((show && !layerIsVisible) || (!show && layerIsVisible)) {
    map.setLayoutProperty(layerName, 'visibility', show ? 'visible' : 'none');
  }
};

export const onToggleStandardMarker = (layer) => (map, show) => {
  const {
    standardMarkerOptions: { markerType },
  } = layer;
  const { hidingAllLayers } = getStore().getState().appControls;
  telemetry(
    telemetry.DEBUG,
    `[onToggleStandardMarker] toggling ${markerType} to ${show ? 'ON' : 'OFF'}`,
  );
  toggleLayerIfRequired(map, markerType, show);
  // 'Standard markers' are all contained in a single layer so hide when hiding all
  // Otherwise the marker types are 'hidden' individually
  // by not existing in the marker cache payload
  toggleLayerIfRequired(map, 'standardMarkerLayer', !hidingAllLayers);
};
